/**
 * Client-side reverse geocoding with multiple providers, caching, and rate limiting.
 * Users select their preferred provider in My Account settings.
 */

const PROVIDERS = {
  nominatim: {
    name: 'Nominatim',
    requiresKey: false,
    buildUrl: (lat, lon, _key, lang) => {
      const params = new URLSearchParams({ lat, lon, format: 'json' });
      if (lang) params.set('accept-language', lang);
      return `https://nominatim.openstreetmap.org/reverse?${params}`;
    },
    parseResponse: (data) => data?.display_name || null,
  },
  google: {
    name: 'Google Maps',
    requiresKey: true,
    buildUrl: (lat, lon, key, lang) => {
      const params = new URLSearchParams({ latlng: `${lat},${lon}`, key });
      if (lang) params.set('language', lang);
      return `https://maps.googleapis.com/maps/api/geocode/json?${params}`;
    },
    parseResponse: (data) => data?.results?.[0]?.formatted_address || null,
  },
  locationiq: {
    name: 'LocationIQ',
    requiresKey: true,
    buildUrl: (lat, lon, key, lang) => {
      const params = new URLSearchParams({ lat, lon, key, format: 'json' });
      if (lang) params.set('accept-language', lang);
      return `https://us1.locationiq.com/v1/reverse?${params}`;
    },
    parseResponse: (data) => data?.display_name || null,
  },
  mapbox: {
    name: 'Mapbox',
    requiresKey: true,
    buildUrl: (lat, lon, key, lang) => {
      const params = new URLSearchParams({
        longitude: lon,
        latitude: lat,
        access_token: key,
      });
      if (lang) params.set('language', lang);
      return `https://api.mapbox.com/search/geocode/v6/reverse?${params}`;
    },
    parseResponse: (data) => data?.features?.[0]?.properties?.full_address || null,
  },
};

// --- Cache (LRU, max 500 entries) ---
const MAX_CACHE = 500;
const cache = new Map();

function cacheKey(provider, lat, lon) {
  return `${provider}:${lat.toFixed(4)},${lon.toFixed(4)}`;
}

function cacheGet(key) {
  const val = cache.get(key);
  if (val !== undefined) {
    cache.delete(key);
    cache.set(key, val);
  }
  return val;
}

function cacheSet(key, value) {
  if (cache.size >= MAX_CACHE) {
    const oldest = cache.keys().next().value;
    cache.delete(oldest);
  }
  cache.set(key, value);
}

// --- Rate limiter (max 2 req/sec) ---
let lastRequestTime = 0;
const MIN_INTERVAL = 500;
const queue = [];
let processing = false;

async function processQueue() {
  if (processing) return;
  processing = true;
  while (queue.length > 0) {
    const { resolve, reject, fn } = queue.shift();
    const now = Date.now();
    const wait = Math.max(0, MIN_INTERVAL - (now - lastRequestTime));
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    lastRequestTime = Date.now();
    try {
      resolve(await fn());
    } catch (err) {
      reject(err);
    }
  }
  processing = false;
}

function enqueue(fn) {
  return new Promise((resolve, reject) => {
    queue.push({ resolve, reject, fn });
    processQueue();
  });
}

// --- Public API ---

export function getProviders() {
  return Object.entries(PROVIDERS).map(([id, p]) => ({
    id,
    name: p.name,
    requiresKey: p.requiresKey,
  }));
}

export async function reverseGeocode(lat, lon, provider, apiKey, language) {
  if (!provider || !PROVIDERS[provider]) return null;
  const config = PROVIDERS[provider];
  if (config.requiresKey && !apiKey) return null;

  const key = cacheKey(provider, lat, lon);
  const cached = cacheGet(key);
  if (cached !== undefined) return cached;

  const address = await enqueue(async () => {
    const url = config.buildUrl(lat, lon, apiKey, language);
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Geocoder ${resp.status}`);
    const data = await resp.json();
    return config.parseResponse(data);
  });

  if (address) cacheSet(key, address);
  return address;
}

export const GEOCODER_LANGUAGES = [
  { value: '', label: 'settings.providerDefault' },
  { value: 'en', label: 'settings.langEnglish' },
  { value: 'ar', label: 'settings.langArabic' },
  { value: 'ku', label: 'settings.langKurdish' },
  { value: 'tr', label: 'settings.langTurkish' },
  { value: 'fa', label: 'settings.langPersian' },
  { value: 'fr', label: 'settings.langFrench' },
];
